/**
 * Phase H (H.3) — Open / focus a per-document preview window.
 *
 * The desktop's main `/documents` window is the SharePoint Explorer
 * (tree | list | preview). Q11 calls for double-clicking a row to spin
 * up a *dedicated* preview window, so the lawyer can have multiple docs
 * side-by-side while keeping the explorer free for browsing.
 *
 * Pattern (per `feedback_keep_working_paths`):
 *   - Window path is `/doc/{docId}` — each id gets its own window
 *     registration. Existing windows are focused; new ones are
 *     registered + opened.
 *   - The window's content is `<DocumentPreview documentId={docId} />`
 *     wrapped in a tiny scroll container — same component the explorer
 *     uses, so the two surfaces stay in lock-step.
 *
 * The hook avoids creating windows on render: the open helper is the
 * only side-effecting call. C-6 (`useNipeWindowSync`) uses the same
 * `WindowContext` API so this just adds a sibling helper.
 */

import { useCallback } from 'react';
import { useWindowContext } from '../components/desktop/WindowContext';
import { DocumentPreview } from '../components/sharepoint/DocumentPreview';

/** Stable path scheme: keep the slash-prefix so it slots into the
 * existing window registry without colliding with the `/documents`
 * three-pane window. */
export function docWindowPath(docId: string): string {
  return `/doc/${docId}`;
}

/** Truncate long titles for the Win95 titlebar without losing identity. */
export function buildDocWindowTitle(rawTitle: string): string {
  const t = (rawTitle ?? '').trim();
  if (t.length === 0) return 'Document';
  if (t.length <= 40) return t;
  return `${t.slice(0, 37)}…`;
}

export interface DocWindowOpener {
  /** Open a window for the given document; focus it if already open. */
  openDocument: (docId: string, title?: string) => void;
}

export function useDocumentWindow(): DocWindowOpener {
  const { getWindow, registerWindow, openWindow, focusWindow } = useWindowContext();

  const openDocument = useCallback(
    (docId: string, title?: string) => {
      if (!docId) return;
      const path = docWindowPath(docId);
      const existing = getWindow(path);
      if (existing) {
        if (existing.state === 'closed' || existing.state === 'minimized') {
          openWindow(path);
        } else {
          focusWindow(path);
        }
        return;
      }
      registerWindow(path, buildDocWindowTitle(title ?? docId), <DocumentPreview documentId={docId} />, {
        width: 720,
        height: 580,
        minWidth: 480,
        minHeight: 360,
        centered: true,
        offsetXPercent: 0.04,
        offsetYPercent: 0.04,
        contentOverflowY: 'hidden',
      });
    },
    [getWindow, registerWindow, openWindow, focusWindow],
  );

  return { openDocument };
}
